import { todayWithSystemTimeZone, convertTimeZone } from '@magicdoorinc/date';
import { createMemo, createSignal, createEffect, onCleanup } from 'solid-js';
import { useCurrentCompany } from '~/contexts/companies/CompaniesContext';
import { useSubscription } from '~/contexts/subscription/SubscriptionContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { useCompanyPaymentAccountsByMode } from '~/hooks/useCompanyPaymentAccountsByMode';
import { SubscriptionType } from '~/swagger/subscriptions/Api';

export const [TrialStatusProvider, useTrialStatus] = createMagicDoorContext('trialStatus', () => {
  const { currentCompany } = useCurrentCompany();
  const paymentAccountsByMode = useCompanyPaymentAccountsByMode();
  const { effectiveSubscription } = useSubscription();

  const [currentTime, setCurrentTime] = createSignal(todayWithSystemTimeZone().toDate());

  const isLoading = createMemo(() => {
    return !currentCompany() || paymentAccountsByMode().isLoading || !effectiveSubscription();
  });

  const isTrialAccount = createMemo(() => {
    const subscription = effectiveSubscription();
    return subscription?.subscriptionType === SubscriptionType.Free;
  });

  const trialAccountUntil = createMemo(() => {
    const company = currentCompany();
    return (company as any)?.trialAccountUntil ? (company as any).trialAccountUntil : null;
  });

  const hasPaymentMethod = createMemo(() => {
    return paymentAccountsByMode().hasPaymentMethod;
  });

  const isTrialExpired = createMemo(() => {
    const trialUntil = trialAccountUntil();
    if (!trialUntil) return false;
    const now = todayWithSystemTimeZone();
    const trialDate = convertTimeZone(trialUntil);
    return now.isAfter(trialDate);
  });

  const daysUntilTrialExpires = createMemo(() => {
    const trialUntil = trialAccountUntil();
    if (!trialUntil) return null;

    currentTime();

    const now = todayWithSystemTimeZone();
    const trialDate = convertTimeZone(trialUntil);
    const diffDays = trialDate.diff(now, 'day', true);

    return diffDays > 0 ? Math.ceil(diffDays) : 0;
  });

  const shouldShowTrialBanner = createMemo(() => {
    if (isLoading()) return false;
    return isTrialAccount() && !hasPaymentMethod() && !isTrialExpired() && daysUntilTrialExpires() !== null;
  });

  const shouldLockoutUser = createMemo(() => {
    if (isLoading()) return false;
    return isTrialAccount() && !hasPaymentMethod() && isTrialExpired();
  });

  createEffect(() => {
    const updateTimeAtMidnight = () => {
      const now = todayWithSystemTimeZone().toDate();
      const tomorrow = todayWithSystemTimeZone().add(1, 'day').startOf('day').toDate();

      const msUntilMidnight = tomorrow.getTime() - now.getTime();

      const timeoutId = setTimeout(() => {
        setCurrentTime(todayWithSystemTimeZone().toDate());
        updateTimeAtMidnight();
      }, msUntilMidnight);

      onCleanup(() => clearTimeout(timeoutId));
    };

    if (isTrialAccount() && !hasPaymentMethod()) {
      updateTimeAtMidnight();
    }
  });

  return {
    isLoading,
    isTrialAccount,
    trialAccountUntil,
    hasPaymentMethod,
    isTrialExpired,
    daysUntilTrialExpires,
    shouldShowTrialBanner,
    shouldLockoutUser,
  };
});
